import apiCore from '../../utils/api-core';

// ── v3 paginated list endpoints ─────────────────────────────────────

export function getCompanies(params) {
    return apiCore.get('/api/v3/companies', { params });
}

export function getCompanyUsers(companyId, params) {
    return apiCore.get(`/api/v3/company/${companyId}/users`, { params });
}

export function getCompanyDepartmentsList(companyId, params) {
    return apiCore.get(`/api/v3/company/${companyId}/departments/list`, { params });
}

export function getCompanyDepartments(companyId) {
    return apiCore.get(`/api/v3/company/${companyId}/departments`);
}

export function getCompanyEmailGroups(companyId, params) {
    return apiCore.get(`/api/v3/company/${companyId}/email-groups`, { params });
}

// ── Company CRUD ─────────────────────────────────────────────────────

export function getAllCompanies(includeInactive = '') {
    const queryParam = includeInactive === '' ? '' : '?includeInactive=false';
    return apiCore.get('/api/v1/companies' + queryParam);
}

export function createCompany(data) {
    return apiCore.post('/api/v1/companies', data);
}

// ── Users (v3 company-scoped) ────────────────────────────────────────

export function createUser(companyId, data) {
    return apiCore.post(`/api/v3/company/${companyId}/users`, data);
}

export function updateUser(companyId, id, data) {
    return apiCore.put(`/api/v3/company/${companyId}/users/${id}`, data);
}

export function deleteUser(companyId, id) {
    return apiCore.delete(`/api/v3/company/${companyId}/users/${id}`);
}

// Assign a pool Vonage number (vonagenumbers.id) as the user's 1-1 texting
// number. GoHire-admin only; releases the current number server-side.
export function assignUserTextingNumber(companyId, id, vonagenumberId) {
    return apiCore.put(`/api/v3/company/${companyId}/users/${id}/texting-number`, { vonagenumberId });
}

// ── Departments (v3 company-scoped) ──────────────────────────────────

export function createDepartment(companyId, data) {
    return apiCore.post(`/api/v3/company/${companyId}/departments`, data);
}

export function getCompanyDepartment(companyId, id) {
    return apiCore.get(`/api/v3/company/${companyId}/departments/${id}`);
}

export function updateDepartment(companyId, id, data) {
    return apiCore.put(`/api/v3/company/${companyId}/departments/${id}`, data);
}

export function deleteDepartment(companyId, id) {
    return apiCore.delete(`/api/v3/company/${companyId}/departments/${id}`);
}

// ── Email Groups (v3 company-scoped) ─────────────────────────────────

export function createEmailGroup(companyId, data) {
    return apiCore.post(`/api/v3/company/${companyId}/email-groups`, data);
}

export function updateEmailGroup(companyId, id, data) {
    return apiCore.put(`/api/v3/company/${companyId}/email-groups/${id}`, data);
}

export function deleteEmailGroup(companyId, id) {
    return apiCore.delete(`/api/v3/company/${companyId}/email-groups/${id}`);
}

// ── System-email recipient overrides (v3, gohire-admin only) ─────────

export function getEmailRecipientRules(companyId) {
    return apiCore.get(`/api/v3/company/${companyId}/email-recipient-rules`);
}

export function upsertEmailRecipientRule(companyId, emailType, data) {
    return apiCore.put(`/api/v3/company/${companyId}/email-recipient-rules/${emailType}`, data);
}

export function deleteEmailRecipientRule(companyId, emailType) {
    return apiCore.delete(`/api/v3/company/${companyId}/email-recipient-rules/${emailType}`);
}

export function sendTestEmailRule(companyId, emailType, data) {
    return apiCore.post(`/api/v3/company/${companyId}/email-recipient-rules/${emailType}/test`, data);
}

// ── AI features per company (v3, gohire-admin only) — ADR-033 ────────

export function getCompanyAiFeatures(companyId) {
    return apiCore.get(`/api/v3/company/${companyId}/ai-features`);
}

export function updateCompanyAiFeature(companyId, featureKey, patch) {
    return apiCore.put(`/api/v3/company/${companyId}/ai-features/${featureKey}`, patch);
}

// ── WhatsApp sender per company (v3, gohire-admin only) — ADR-037 ─────

export function getCompanyWhatsApp(companyId) {
    return apiCore.get(`/api/v3/company/${companyId}/whatsapp`);
}

export function createCompanyWhatsAppSender(companyId, data) {
    return apiCore.post(`/api/v3/company/${companyId}/whatsapp/senders`, data);
}

export function updateCompanyWhatsAppSettings(companyId, data) {
    return apiCore.put(`/api/v3/company/${companyId}/whatsapp/settings`, data);
}

// Edit display name / Apply-By-Text bot (abtBotId: uuid | null) / WABA id / status.
export function updateCompanyWhatsAppSender(companyId, botmapId, data) {
    return apiCore.put(`/api/v3/company/${companyId}/whatsapp/senders/${botmapId}`, data);
}

// Retire the number: history stays read-only, held + scheduled WhatsApp messages are cancelled.
export function removeCompanyWhatsAppSender(companyId, botmapId) {
    return apiCore.delete(`/api/v3/company/${companyId}/whatsapp/senders/${botmapId}`);
}

// Move the number to another company ({ targetCompanyId, abtBotId?, displayName? }).
export function moveCompanyWhatsAppSender(companyId, botmapId, data) {
    return apiCore.post(`/api/v3/company/${companyId}/whatsapp/senders/${botmapId}/move`, data);
}

export function getCompanyWhatsAppSenderUsers(companyId, botmapId) {
    return apiCore.get(`/api/v3/company/${companyId}/whatsapp/senders/${botmapId}/users`);
}

// Put a user on (enabled: true) or take a user off (false) the number.
export function setCompanyWhatsAppSenderUser(companyId, botmapId, userId, enabled) {
    return apiCore.put(`/api/v3/company/${companyId}/whatsapp/senders/${botmapId}/users/${userId}`, { enabled });
}

// ── WhatsApp templates (v3, gohire-admin only) — ADR-037 phase 0A ─────

// Pull the sender's templates from Infobip → { synced, added, updated, removed, templates }.
export function syncCompanyWhatsAppTemplates(companyId, botmapId) {
    return apiCore.post(`/api/v3/company/${companyId}/whatsapp/senders/${botmapId}/templates/sync`, {});
}

export function getCompanyWhatsAppTemplates(companyId, botmapId) {
    return apiCore.get(`/api/v3/company/${companyId}/whatsapp/senders/${botmapId}/templates`);
}

// Save a mapping: { kind: 'reengage' | 'interview_invite' | 'interview_reminder' | null, params: [...] }.
export function updateCompanyWhatsAppTemplate(companyId, templateId, data) {
    return apiCore.put(`/api/v3/company/${companyId}/whatsapp/templates/${templateId}`, data);
}

// Server-rendered preview: { contactId?, params?, inputs? } → { text }.
export function previewCompanyWhatsAppTemplate(companyId, templateId, data) {
    return apiCore.post(`/api/v3/company/${companyId}/whatsapp/templates/${templateId}/preview`, data);
}

// { standard: [{ key, label, group }], custom: [key] }
export function getCompanyWhatsAppMergeFields(companyId) {
    return apiCore.get(`/api/v3/company/${companyId}/whatsapp/merge-fields`);
}

// ── Avatar ───────────────────────────────────────────────────────────

export function uploadUserAvatar(userId, formData) {
    return apiCore.post(`/api/v3/users/${userId}/avatar`, formData);
}

// ── Calendar / integrations ─────────────────────────────────────────

export function getUserSettings(userId) {
    return apiCore.get(`/api/v1/integrations/settings/${userId}`);
}

export function getCalendarPreview(userId, from) {
    return apiCore.get(`/api/v1/integrations/calendar-preview/${userId}/${encodeURIComponent(from)}`);
}
